import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, getDocs, getDoc, query, orderBy, where, doc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { ChangeOrder, ProposalBuilderData } from '../types';
import { AppHeader } from '../components/ui/AppHeader';
import { ChevronRight, FileText, Search, RotateCcw, Copy, Trash2, Lock, Package, Download, GitCompare } from 'lucide-react';
import { applyProfitMargin } from '../utils/proposalCalculations';
import { getSignedCommercialListTotal } from '../utils/proposalPublicGrandTotals';
import { speciesTimberKitRawDelta } from '../utils/proposalSpeciesProduction';
import CopyProposalModal from '../components/proposals/CopyProposalModal';
import PublicProposalCompareModal from '../components/proposals/PublicProposalCompareModal';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchProposalsHub } from '../utils/hubSearch/searchSecondaryHubs';
import { HubSearchResult } from '../types/hubSearch';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './ProposalsSummaryPage.css';

const ProposalsSummaryPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [proposals, setProposals] = useState<(ProposalBuilderData & { id: string })[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<'all' | 'kit' | 'custom'>('all');
    const [copySourceProposal, setCopySourceProposal] = useState<(ProposalBuilderData & { id: string }) | null>(null);
    const [showCompare, setShowCompare] = useState(() => searchParams.get('compare') === '1');
    const compareLeftParam = searchParams.get('left');
    const [showQuickSearch, setShowQuickSearch] = useState(false);
    const [approvedCoByDeal, setApprovedCoByDeal] = useState<Record<string, number>>({});

    useHubQuickSearchHotkey({
        onOpen: () => setShowQuickSearch(true),
        blocked: showQuickSearch || !!copySourceProposal || showCompare,
    });

    const openCompare = useCallback(() => {
        setShowCompare(true);
    }, []);

    const closeCompare = useCallback(() => {
        setShowCompare(false);
        if (searchParams.get('compare') || searchParams.get('left')) {
            const next = new URLSearchParams(searchParams);
            next.delete('compare');
            next.delete('left');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        if (searchParams.get('compare') === '1') {
            setShowCompare(true);
        }
    }, [searchParams]);

    const getProposalQuickSearchResults = useCallback(
        (query: string) => searchProposalsHub(query, proposals),
        [proposals],
    );

    const handleProposalQuickSearchSelect = useCallback(
        (result: HubSearchResult) => {
            const proposal = result.payload as ProposalBuilderData & { id: string };
            navigate(`/proposal-builder/${proposal.dealId || proposal.id}`);
        },
        [navigate],
    );

    // Handler to un-approve a proposal
    const handleUnapprove = async (e: React.MouseEvent, proposalId: string) => {
        e.preventDefault();
        e.stopPropagation();
        if (!window.confirm('Are you sure you want to revert this proposal to draft status?')) return;

        try {
            await updateDoc(doc(db, 'proposalBuilders', proposalId), {
                status: 'draft',
                signature: null,
                approvedAt: null,
                updatedAt: Timestamp.now(),
            });

            setProposals(prev => prev.map(p =>
                p.id === proposalId ? { ...p, status: 'draft', signature: undefined, approvedAt: undefined } : p
            ));
        } catch (error) {
            console.error('Error un-approving proposal:', error);
            alert('Failed to un-approve proposal.');
        }
    };

    const handleDuplicate = (e: React.MouseEvent, proposal: ProposalBuilderData & { id: string }) => {
        e.preventDefault();
        e.stopPropagation();
        setCopySourceProposal(proposal);
    };

    // Handler to delete a proposal
    const handleDelete = async (e: React.MouseEvent, proposal: ProposalBuilderData & { id: string }) => {
        e.preventDefault();
        e.stopPropagation();

        const confirmMsg = proposal.status === 'approved'
            ? 'This proposal is APPROVED. Are you sure you want to delete it? This action cannot be undone.'
            : 'Are you sure you want to delete this proposal? This action cannot be undone.';

        if (!window.confirm(confirmMsg)) return;

        try {
            // If there's a linked deal, clear the proposalId so it reverts to "Build Proposal" button
            if (proposal.dealId) {
                try {
                    const dealRef = doc(db, 'enhancedDeals', proposal.dealId);
                    const dealSnap = await getDoc(dealRef);
                    if (dealSnap.exists()) {
                        await updateDoc(dealRef, {
                            proposalId: null,
                            outcome: 'active',
                            stageId: 'qualified', // Move back to qualified stage
                            updatedAt: Timestamp.now(),
                        });
                    }
                } catch (dealErr) {
                    console.error('Error updating deal:', dealErr);
                }
            }

            // Delete the proposal
            await deleteDoc(doc(db, 'proposalBuilders', proposal.id));

            // Update local state
            setProposals(prev => prev.filter(p => p.id !== proposal.id));
        } catch (error) {
            console.error('Error deleting proposal:', error);
            alert('Failed to delete proposal.');
        }
    };

    useEffect(() => {
        const fetchProposals = async () => {
            try {
                const proposalsRef = collection(db, 'proposalBuilders');
                const q = query(proposalsRef, orderBy('updatedAt', 'desc'));
                const snapshot = await getDocs(q);

                const proposalData = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                })) as (ProposalBuilderData & { id: string })[];

                setProposals(proposalData);
            } catch (error) {
                console.error('Error fetching proposals:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProposals();
    }, []);

    // Approved change-order deltas per deal — surfaces a CO-adjusted contract total
    // beside the original signed proposal amount (the base amount is never changed).
    useEffect(() => {
        const fetchApprovedChangeOrders = async () => {
            try {
                const snap = await getDocs(query(collection(db, 'changeOrders'), where('status', '==', 'approved')));
                const map: Record<string, number> = {};
                snap.docs.forEach(d => {
                    const co = d.data() as ChangeOrder;
                    if (!co.dealId) return;
                    map[co.dealId] = (map[co.dealId] || 0) + (Number(co.deltaAmount) || 0);
                });
                setApprovedCoByDeal(map);
            } catch (error) {
                console.warn('Error fetching change orders for proposals summary:', error);
            }
        };
        fetchApprovedChangeOrders();
    }, []);

    const filteredProposals = useMemo(() => {
        return proposals.filter(p => {
            const q = searchQuery.toLowerCase();
            const matchesSearch = searchQuery === '' ||
                p.clientName?.toLowerCase().includes(q) ||
                p.slug?.toLowerCase().includes(q) ||
                p.projectName?.toLowerCase().includes(q);

            // The 'draft' tab also catches rows where status is missing entirely
            // — those collapse to "Draft" in the row UI via getStatusLabel.
            const matchesStatus =
                statusFilter === 'all' ||
                p.status === statusFilter ||
                (statusFilter === 'draft' && !p.status);

            // Type filter: kit vs custom
            const matchesType = typeFilter === 'all' ||
                (typeFilter === 'kit' && p.isKitProposal) ||
                (typeFilter === 'custom' && !p.isKitProposal);

            return matchesSearch && matchesStatus && matchesType;
        });
    }, [proposals, searchQuery, statusFilter, typeFilter]);

    const getStatusPill = (status?: string) => {
        if (status === 'approved') return 'completed';
        if (status === 'sent') return 'in-progress';
        if (status === 'opened' || status === 'viewed') return 'review';
        return 'pending';
    };

    const getStatusLabel = (status?: string) => {
        switch (status) {
            case 'approved': return 'Approved';
            case 'sent': return 'Sent';
            case 'opened':
            case 'viewed':
                return 'Opened';
            default: return 'Draft';
        }
    };

    const formatDate = (date: any) => {
        if (!date) return '-';
        try {
            // Handle Firestore Timestamp with toDate()
            if (date && typeof date.toDate === 'function') {
                return date.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
            // Handle Firestore Timestamp as plain object {seconds, nanoseconds}
            if (date && typeof date.seconds === 'number') {
                return new Date(date.seconds * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
            // Handle Date object or date string
            const d = new Date(date);
            if (isNaN(d.getTime())) return '-';
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
            return '-';
        }
    };

    const formatCurrency = (amount?: number) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const calculateTotal = (proposal: ProposalBuilderData) => {
        const signedTotal = getSignedCommercialListTotal(proposal);
        if (signedTotal != null) {
            return signedTotal;
        }

        // Use stored displayTotal if available (set when proposal is saved)
        if ((proposal as any).displayTotal) {
            return (proposal as any).displayTotal;
        }

        // Fallback calculation for older proposals without displayTotal
        if (!proposal.nodeSets) return 0;
        const profitMargin = proposal.profitMargin || 20;

        // For kit proposals, use kitPricing based on activeSpecies
        if (proposal.isKitProposal && proposal.kitPricing && proposal.kitPricing.length > 0) {
            const activeSpecies = proposal.activeSpecies || proposal.offeredSpecies?.[0] || proposal.kitPricing[0]?.speciesName;
            const selectedPricing = proposal.kitPricing.find(kp => kp.speciesName === activeSpecies);

            if (selectedPricing) {
                // Kit price already includes profit
                const kitBillable = selectedPricing.totalCost;

                // Only include install if it's NOT optional
                // When installOptional=true, the client can choose to exclude it
                let installBillable = 0;
                if (!proposal.installOptional) {
                    let installRawTotal = 0;
                    proposal.nodeSets.forEach(ns => {
                        const qty = ns.quantity || 1;
                        installRawTotal += (ns.install.total + ns.travel.total) * qty;
                    });
                    installBillable = applyProfitMargin(installRawTotal, profitMargin);
                }

                return Math.round(kitBillable + installBillable);
            }
        }

        // Standard calculation for custom proposals
        const selectedSpecies = (proposal as any).selectedSpecies as Record<string, string> | undefined;

        return proposal.nodeSets.reduce((total, ns) => {
            const qty = ns.quantity || 1;
            const rawCost = ns.design.total + ns.materials.total + ns.production.total +
                ns.freight.total + ns.install.total + ns.travel.total + ns.overhead.total + (ns.install.overheadTotal || 0);
            let baseCost = Math.round(applyProfitMargin(rawCost, profitMargin) * qty);

            // Adjust for species selection if applicable
            if (selectedSpecies && selectedSpecies[ns.id] && selectedSpecies[ns.id] !== 'default') {
                const { rawDelta } = speciesTimberKitRawDelta(ns, selectedSpecies[ns.id]);
                if (rawDelta) {
                    baseCost += Math.round(applyProfitMargin(rawDelta, profitMargin) * qty);
                }
            }

            return total + baseCost;
        }, 0);
    };

    const statusCounts = useMemo(() => {
        return {
            all: proposals.length,
            draft: proposals.filter(p => !p.status || p.status === 'draft').length,
            sent: proposals.filter(p => p.status === 'sent').length,
            opened: proposals.filter(p => p.status === 'opened').length,
            approved: proposals.filter(p => p.status === 'approved').length,
        };
    }, [proposals]);

    const typeCounts = useMemo(() => {
        return {
            all: proposals.length,
            kit: proposals.filter(p => p.isKitProposal).length,
            custom: proposals.filter(p => !p.isKitProposal).length,
        };
    }, [proposals]);

    return (
        <div className="ops-hub proposals-hub">
            <AppHeader />
            <div className="ops-hub-layout proposals-hub-layout">
                <div className="ops-hub-main">
                    <div className="ops-hub-toolbar proposals-hub-toolbar">
                        <div className="ops-hub-toolbar-left">
                            <h2 className="ops-hub-title">Proposals</h2>
                            <div className="ops-hub-stats">
                                <button
                                    type="button"
                                    className={`ops-stat${statusFilter === 'all' ? ' is-active' : ''}`}
                                    onClick={() => setStatusFilter('all')}
                                >
                                    {statusCounts.all} total
                                </button>
                                <button
                                    type="button"
                                    className={`ops-stat${statusFilter === 'draft' ? ' is-active' : ''}`}
                                    onClick={() => setStatusFilter('draft')}
                                >
                                    {statusCounts.draft} draft
                                </button>
                                <button
                                    type="button"
                                    className={`ops-stat ops-stat-sent${statusFilter === 'sent' ? ' is-active' : ''}`}
                                    onClick={() => setStatusFilter('sent')}
                                >
                                    {statusCounts.sent} sent
                                </button>
                                <button
                                    type="button"
                                    className={`ops-stat ops-stat-opened${statusFilter === 'opened' ? ' is-active' : ''}`}
                                    onClick={() => setStatusFilter('opened')}
                                >
                                    {statusCounts.opened} opened
                                </button>
                                <button
                                    type="button"
                                    className={`ops-stat ops-stat-approved${statusFilter === 'approved' ? ' is-active' : ''}`}
                                    onClick={() => setStatusFilter('approved')}
                                >
                                    {statusCounts.approved} approved
                                </button>
                            </div>
                        </div>
                        <div className="ops-hub-toolbar-right">
                            <div className="proposals-hub-search-wrap">
                                <Search size={14} className="proposals-hub-search-icon" />
                                <input
                                    className="proposals-hub-search"
                                    type="search"
                                    placeholder="Search client, project, or slug…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                            <div className="ops-hub-views">
                                <button
                                    type="button"
                                    className={`ops-view-btn${typeFilter === 'all' ? ' active' : ''}`}
                                    onClick={() => setTypeFilter('all')}
                                >
                                    All
                                </button>
                                <button
                                    type="button"
                                    className={`ops-view-btn${typeFilter === 'kit' ? ' active' : ''}`}
                                    onClick={() => setTypeFilter('kit')}
                                >
                                    <Package size={12} />
                                    Kit ({typeCounts.kit})
                                </button>
                                <button
                                    type="button"
                                    className={`ops-view-btn${typeFilter === 'custom' ? ' active' : ''}`}
                                    onClick={() => setTypeFilter('custom')}
                                >
                                    Custom ({typeCounts.custom})
                                </button>
                            </div>
                            <button
                                type="button"
                                className="proposals-hub-ghost-btn"
                                onClick={() => navigate('/proposals/download-history')}
                            >
                                <Download size={14} />
                                Download History
                            </button>
                            <button
                                type="button"
                                className="ops-new-btn"
                                onClick={openCompare}
                            >
                                <GitCompare size={14} />
                                Compare pages
                            </button>
                        </div>
                    </div>

                    <div className="ops-hub-content proposals-hub-content">
                        {loading ? (
                            <div className="proposals-hub-loading">
                                <div className="ops-hub-loading-bar" />
                                <p>Loading proposals…</p>
                            </div>
                        ) : filteredProposals.length === 0 ? (
                            <div className="proposals-hub-empty">
                                <FileText size={28} />
                                <p>No proposals found</p>
                            </div>
                        ) : (
                            <div className="proposals-hub-table-wrap">
                                <table className="proposals-hub-table">
                                    <thead>
                                        <tr>
                                            <th>Client</th>
                                            <th>Project</th>
                                            <th>Status</th>
                                            <th>Total</th>
                                            <th>Created</th>
                                            <th>Updated</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProposals.map(proposal => (
                                            <tr
                                                key={proposal.id}
                                                onClick={() => navigate(`/proposal-builder/${proposal.dealId || proposal.id}`)}
                                            >
                                                <td className="client-name">
                                                    <span className="client-name-cell">
                                                        {proposal.isKitProposal && (
                                                            <span className="kit-badge-inline" title={proposal.sourceKitName || 'Kit Proposal'}>
                                                                <Package size={12} />
                                                            </span>
                                                        )}
                                                        {proposal.clientName || 'Unnamed Client'}
                                                    </span>
                                                </td>
                                                <td className="project-name">{proposal.slug || 'Untitled'}</td>
                                                <td>
                                                    <span className="proposals-hub-status">
                                                        <span className="pm-status-pill" data-status={getStatusPill(proposal.status)}>
                                                            {getStatusLabel(proposal.status)}
                                                        </span>
                                                        {proposal.pricingLocked && (
                                                            <span className="lock-indicator" title="Pricing locked">
                                                                <Lock size={11} />
                                                            </span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="total">
                                                    {formatCurrency(calculateTotal(proposal))}
                                                    {(() => {
                                                        const coDelta = proposal.dealId ? (approvedCoByDeal[proposal.dealId] || 0) : 0;
                                                        if (!coDelta) return null;
                                                        const adjusted = calculateTotal(proposal) + coDelta;
                                                        return (
                                                            <span
                                                                className="total-with-cos"
                                                                title="Includes approved change orders"
                                                                style={{ color: coDelta < 0 ? '#b4453a' : '#2c6b3c' }}
                                                            >
                                                                {formatCurrency(adjusted)} incl. COs
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="date">{formatDate(proposal.createdAt)}</td>
                                                <td className="date">{formatDate(proposal.updatedAt)}</td>
                                                <td>
                                                    <div className="proposals-hub-actions">
                                                        <button
                                                            className="proposals-hub-action is-duplicate"
                                                            onClick={(e) => handleDuplicate(e, proposal)}
                                                            title="Duplicate proposal"
                                                            aria-label="Duplicate proposal"
                                                        >
                                                            <Copy size={14} />
                                                        </button>
                                                        <button
                                                            className="proposals-hub-action is-delete"
                                                            onClick={(e) => handleDelete(e, proposal)}
                                                            title="Delete proposal"
                                                            aria-label="Delete proposal"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                        {proposal.status === 'approved' && (
                                                            <button
                                                                className="proposals-hub-action is-unapprove"
                                                                onClick={(e) => handleUnapprove(e, proposal.id)}
                                                                title="Revert to draft"
                                                                aria-label="Revert to draft"
                                                            >
                                                                <RotateCcw size={14} />
                                                            </button>
                                                        )}
                                                        <ChevronRight size={16} className="row-chevron" />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {copySourceProposal && (
                <CopyProposalModal
                    open={!!copySourceProposal}
                    onClose={() => setCopySourceProposal(null)}
                    sourceProposal={copySourceProposal}
                    onCopied={(newDealId) => {
                        setCopySourceProposal(null);
                        navigate(`/proposal-builder/${newDealId}`);
                    }}
                />
            )}

            <PublicProposalCompareModal
                open={showCompare}
                proposals={proposals}
                initialLeftId={compareLeftParam}
                onClose={closeCompare}
            />

            <HubQuickSearch
                open={showQuickSearch}
                onClose={() => setShowQuickSearch(false)}
                getResults={getProposalQuickSearchResults}
                onSelect={handleProposalQuickSearchSelect}
                placeholder="Search proposals by client or slug…"
                ariaLabel="Proposals quick search"
                renderIcon={() => <FileText size={16} />}
            />
        </div>
    );
};

export default ProposalsSummaryPage;
